import React, { useCallback, useRef, useState, useEffect } from 'react';
import 'antd/dist/antd.min.css';
import './upload.css';
import './ImageEditor.css';
import { MdSwapCalls, MdDragIndicator } from "react-icons/md";
import { LuRefreshCcw } from "react-icons/lu";

import {
  Tooltip,
  Upload,
  Modal,
  Input,
  Radio,
  Typography,
  Form,
  message,
  Image,
  Spin,
  Row,
  Col,
  Button,
  Result,
  Checkbox,
  Skeleton,
  Progress
} from 'antd';
import { LoadingOutlined,ReloadOutlined } from '@ant-design/icons';
import update from 'immutability-helper';
import { DndProvider, useDrag, useDrop, useDragLayer } from 'react-dnd';
import axios from 'axios';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { updateProjectImage, updateProjectImageWithoutProjectId } from '../../services/projectServices';
import { MEDIA_UPLOAD_URL, MEDIA_UPLOAD_URL_IMAGE, INSPECTION_URL, MEDIA_UPLOAD_URL_VIDEO } from '../../Helper';
import Swal from 'sweetalert2';
import Fancybox from './fancybox';
import { Fancybox as NativeFancybox } from "@fancyapps/ui/dist/fancybox.esm.js";
import { EyeOutlined, SmileOutlined, PlayCircleOutlined, PictureOutlined, VideoCameraOutlined, DeleteOutlined, DragOutlined, CheckOutlined, CloseOutlined, UndoOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as getCompanycamPhotosService from '../../services/CompanyCam';
import * as getCompanycamProPhotosService from '../../services/CompnyCamImgs';
import * as companyCamtokenSave from '../../services/companyCamtokenService';
import CustomSpinner from '../../components/spinner';
import FdCamEditor from '../../components/ImageEditor/FdCamEditor';
import { useDispatch } from 'react-redux';
import { getProjects } from '../../services/projectServices';
import FolderIMage from "../../assets/images/blue-directory-icon.png";
import heic2any from 'heic2any';

// Moving these outside App to prevent accidental resets during re-renders
let activeHeicConversionCount = 0;
let conversionMessageKey = 'heic_converting_msg';

const { Item } = Form;
const type = 'DragableUploadList';
const { Title } = Typography;
const { Search } = Input;

// Global Auto Scroll Manager
const AutoScrollManager = () => {
  const scrollAnimationRef = useRef(null);
  const { isDragging, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset(),
  }));

  const [showIndicator, setShowIndicator] = useState(null);

  const autoScroll = useCallback(() => {
    if (!isDragging || !currentOffset) {
      setShowIndicator(null);
      return;
    }

    const windowHeight = window.innerHeight;
    const scrollThreshold = 240;
    const scrollSpeed = 18;

    if (currentOffset.y < scrollThreshold) {
      setShowIndicator('top');
      window.scrollBy(0, -scrollSpeed);
      scrollAnimationRef.current = requestAnimationFrame(autoScroll);
    } else if (currentOffset.y > windowHeight - scrollThreshold) {
      setShowIndicator('bottom');
      window.scrollBy(0, scrollSpeed);
      scrollAnimationRef.current = requestAnimationFrame(autoScroll);
    } else {
      setShowIndicator(null);
      scrollAnimationRef.current = requestAnimationFrame(autoScroll);
    }
  }, [isDragging, currentOffset]);

  useEffect(() => {
    if (isDragging) {
      scrollAnimationRef.current = requestAnimationFrame(autoScroll);
    } else {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
      setShowIndicator(null);
    }

    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, [isDragging, autoScroll]);

  if (!showIndicator) return null;

  return (
    <div className={`drag-scroll-indicator drag-scroll-${showIndicator}`}>
      <div className="scroll-arrow">{showIndicator === 'top' ? '↑' : '↓'}</div>
      <span>{showIndicator === 'top' ? 'Scrolling Up' : 'Scrolling Down'}</span>
    </div>
  );
};

// Custom Drag Layer for showing multiple images during drag
const CustomDragLayer = () => {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (!isDragging || !currentOffset || !item?.selectedFiles) {
    return null;
  }

  const { x, y } = currentOffset;
  const selectedFiles = item.selectedFiles || [];

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 10000,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: x,
          top: y,
          transform: 'translate(0, 0)',
        }}
      >
        <div className="custom-drag-preview">
          {selectedFiles.map((file, idx) => (
            <div
              key={file.uid}
              className="custom-drag-preview-item"
              style={{
                transform: `translate(${idx * 8}px, ${idx * 8}px) rotate(${(idx - Math.floor(selectedFiles.length / 2)) * 2}deg)`,
                zIndex: selectedFiles.length - idx,
              }}
            >
              <img src={file.url || file.thumbUrl} alt="" />
            </div>
          ))}
          {selectedFiles.length > 1 && (
            <div className="custom-drag-preview-badge">
              {selectedFiles.length} images
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DragableUploadListItem = ({ originNode, moveRow, file, fileList, isReorderMode, selectedForDrag, onSelectForDrag }) => {
  const ref = useRef(null);

  const index = fileList.indexOf(file);
  const isSelected = selectedForDrag.includes(file.uid);
  const selectedIndex = selectedForDrag.indexOf(file.uid);
  
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};

      if (dragIndex === index) {
        return {};
      }

      return {
        isOver: monitor.isOver(),
        dropClassName:
          dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },

    drop: (item) => {
      moveRow(item.index, index, item.selectedItems);
    },
  });
  
  const [{ isDragging }, drag, preview] = useDrag({
    type,
    item: () => {
      const selectedItems = isSelected ? selectedForDrag : [file.uid];
      return {
        index,
        selectedItems,
        selectedFiles: fileList.filter(f => selectedItems.includes(f.uid)),
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),

  });
  
  drop(drag(ref));

  const errorNode = (
    <Tooltip title="Upload Error">{originNode.props.children}</Tooltip>
  );
  
  return (
    <div
      ref={ref}
      className={`ant-upload-draggable-list-item ${isOver ? dropClassName : ''} ${isSelected ? 'selected-for-drag' : ''} ${isDragging ? 'dragging' : ''}`}
      style={{
        cursor: 'move',
        opacity: isDragging && isSelected && isReorderMode ? 0.3 : 1,
      }}
    >
      {isReorderMode && (
        <div className="drag-checkbox-wrapper">
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelectForDrag(file.uid, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
          {isSelected && (
            <span className="drag-selection-number">{selectedIndex + 1}</span>
          )}
        </div>
      )}
      {file.status === 'error' ? errorNode : originNode}
    </div>
  );
};
// Sortable item wrapper for video attachments
const SortableVideoUploadItem = ({ file, children }) => {
  const fileId = String(file.uid || file.id || file.media_id);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: fileId,
    disabled: file.status === 'uploading'
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative',
    height: '100%',
    width: '100%',
  };

  return (
    <div ref={setNodeRef} style={style} className={`sortable-video-card-container ${isDragging ? 'is-video-dragging' : ''}`}>
      {typeof children === 'function' ? children({ attributes, listeners, isDragging }) : children}
    </div>
  );
};

const progress = {
  strokeWidth: 15,
  format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
};

const App = (props) => {
  const [projects, setProjects] = useState([]);
  const takefileList = props.takefileList;
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const handleCancel = () => setPreviewVisible(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFDCamModalOpen, setIsFDCamModalOpen] = useState(false);
  const [companyCam, setCompanycam] = useState([]);
  const [companyCamProImg, setCompanycamProImg] = useState([]);
  const [companyCamHeading, setCompanycamHeading] = useState('');
  const [CompanycamAddressCity, setCompanycamAddressCity] = useState('');
  const [CompanycamAddressPostal, setCompanycamAddressPostal] = useState('');
  const [CompanycamAddressState, setCompanycamAddressState] = useState('');
  const [CompanycamAddressStreet, setCompanycamAddressStreet] = useState('');
  const [selectedImages, setselectedImages] = useState([]);
  const [active, setActive] = useState(null);
  const [camLoader, setCamLoader] = useState(false);
  const [uploading, setuploading] = useState(false);
  const [cCprogress, setCcprogress] = useState(0);
  const [fDCamImages, setFDCamImages] = useState([]);
  const [fdCamSelectedVideos, setfdCamSelectedVideos] = useState([]); // Selected videos for report
  const [videoFileList, setVideoFileList] = useState([]); // Separate list for videos
  const [fdCamView, setFdCamView] = useState('photos'); // 'photos' | 'videos'
  const [fdCamModalType, setFdCamModalType] = useState('photos'); // controls FDCam modal focus
  const [selectImagesFDCam, setSelectImagesFDCam] = useState(false);
  const [selectVideosFDCam, setSelectVideosFDCam] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [fdCamLoader, setFdCamLoader] = useState(false);
  const [fdCamSelectedProject, setfdCamSelectedProjects] = useState(null);
  const [companyCamToken, setcompanyCamToken] = useState('');
  const [hideFdcamButton, setHideFdcamButton] = useState(true);
  const [page, setPage] = useState(1);
  const [isImgEditorShown, setIsImgEditorShown] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sidebarRef = useRef(null);
  const saveTimeoutRef = useRef({});
  
  // Reorder mode states
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [selectedForDrag, setSelectedForDrag] = useState([]);

  // Video Reorder states & sensors (@dnd-kit)
  const [hasVideoOrderChanged, setHasVideoOrderChanged] = useState(false);
  const [savingVideoOrder, setSavingVideoOrder] = useState(false);
  const videoSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Token Modal
  const [isModalToken, setIsModalToken] = useState(false);
  const [isModalHelp, setIsModalHelp] = useState(false);
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [fdForm] = Form.useForm();
  var dispatch = useDispatch();
  const handleDescriptionChange = (file_uid, caption) => {
    const newAllImageDescription = {
      ...(props.values.Allimagedescription || {}),
      [file_uid]: {
        caption: caption,
      },
    };
    // Direct mutation for immediate availability (same pattern as images)
    props.values.Allimagedescription = newAllImageDescription;
    // Also persist via Redux if handleFill is available
    if (props.handleFill) {
      props.handleFill({ Allimagedescription: newAllImageDescription });
    }

    let media_id = file_uid;
    let shouldSaveToLaravel = false;

    // Check if it's in videoFileList (Laravel)
    const videoFile = videoFileList.find(f => f.uid === file_uid);
    if (videoFile) {
      if (videoFile.media_id) media_id = videoFile.media_id;
      shouldSaveToLaravel = true;
    }

    // Check if it's in fileList (Laravel)
    const imageFile = fileList.find(f => f.uid === file_uid);
    let isFdcamImage = false;
    if (imageFile) {
      if (imageFile.media_id) media_id = imageFile.media_id;
      if (imageFile.isLaravel) {
        shouldSaveToLaravel = true;
      } else if (imageFile.isFdcam) {
        isFdcamImage = true;
      }
    }

    if (saveTimeoutRef.current[file_uid]) {
      clearTimeout(saveTimeoutRef.current[file_uid]);
    }

    saveTimeoutRef.current[file_uid] = setTimeout(async () => {
      if (shouldSaveToLaravel && media_id && !isNaN(media_id)) {
        try {
          await axios.patch(`${MEDIA_UPLOAD_URL_IMAGE}/${media_id}`, {
            description: caption
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
            }
          });
        } catch (error) {
          console.error("Failed to autosave description:", error);
        }
      } else if (isFdcamImage && media_id && !isNaN(media_id)) {
        try {
          await dispatch(updateProjectImageWithoutProjectId(media_id, {
            description: caption
          }));
        } catch (error) {
          console.error("Failed to autosave FDCam description:", error);
        }
      }
    }, 1000); // 1-second debounce delay
  };
  const normFile = (e) => {
    console.log('Upload event:', e);

    if (Array.isArray(e)) {
      return e && e.fileList;
    }

    return e && e.fileList;
  };
  const handleSelectForDrag = (uid, checked) => {
    if (checked) {
      setSelectedForDrag(prev => [...prev, uid]);
    } else {
      setSelectedForDrag(prev => prev.filter(id => id !== uid));
    }
  };

  const toggleReorderMode = () => {
    setIsReorderMode(!isReorderMode);
    if (isReorderMode) {
      setSelectedForDrag([]);
    }
  };

  const moveRow = useCallback(
    (dragIndex, hoverIndex, selectedItems) => {
      const isMultiDrag = selectedItems && selectedItems.length > 1;
      
      if (isMultiDrag) {
        // Multi-select drag
        const selectedFiles = fileList.filter(file => selectedItems.includes(file.uid));
        const unselectedFiles = fileList.filter(file => !selectedItems.includes(file.uid));
        
        // Insert selected files at the hover position
        const newFileList = [...unselectedFiles];
        newFileList.splice(hoverIndex, 0, ...selectedFiles);
        
        setFileList(newFileList);
        if (takefileList) {
          takefileList(newFileList);
        }
      } else {
        // Single drag
        const dragRow = fileList[dragIndex];
        const newFileList = update(fileList, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        });
        setFileList(newFileList);
        if (takefileList) {
          takefileList(newFileList);
        }
        if (props.handleFill) {
          props.handleFill({ attachments: newFileList });
        }
      }
      
      // Clear selection after any drag completes
      if (selectedForDrag.length > 0) {
        setSelectedForDrag([]);
      }
      
      // Exit reorder mode and show success message
      if (isReorderMode) {
        setIsReorderMode(false);
        
        // Show success message
        const imageCount = isMultiDrag ? selectedItems.length : 1;
        message.success({
          content: `${imageCount} ${imageCount === 1 ? 'image' : 'images'} reordered successfully!`,
          duration: 3,
        });
      }
    },
    [fileList, takefileList, selectedForDrag, isReorderMode]
  );

  // Video Drag & Drop End Handler (@dnd-kit)
  const handleVideoDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = videoFileList.findIndex(
      v => String(v.uid || v.id || v.media_id) === String(active.id)
    );
    const newIndex = videoFileList.findIndex(
      v => String(v.uid || v.id || v.media_id) === String(over.id)
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedList = arrayMove(videoFileList, oldIndex, newIndex);
      setVideoFileList(reorderedList);
      setHasVideoOrderChanged(true);
    }
  };

  const handleSaveVideoOrder = () => {
    setSavingVideoOrder(true);
    if (props.handleFill) {
      props.handleFill({ video_attachments: videoFileList });
    }
    setTimeout(() => {
      setSavingVideoOrder(false);
      setHasVideoOrderChanged(false);
      message.success({
        content: `Video order saved successfully! 🎬`,
        duration: 3,
        style: { marginTop: '20px' },
      });
    }, 400);
  };

  const handleCancelVideoOrder = () => {
    const originalAttachments = props.values.video_attachments ? props.values.video_attachments : [];
    setVideoFileList(originalAttachments);
    setHasVideoOrderChanged(false);
    message.info({
      content: 'Video order changes reset.',
      duration: 2,
    });
  };

  const fetchCompanycam = (params = {}) => {
    dispatch(getCompanycamPhotosService.getCompanycamPhotos())
      .then((res) => {
        setCompanycam(res);
      })
      .catch((err) => {
        setIsModalToken(true);
        setIsModalOpen(false);
      });
  };
  // Inside the ImageGallery component

  const setCompanycamToken = (value) => {
    dispatch(companyCamtokenSave.companyCamtokenService(value.companycam_token))
      .then((res) => {
        if (res.data.companycam_token === 'invalid') {
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'You have entered invalid token!',
            confirmButtonText: 'Help',
            allowOutsideClick: false,
          }).then((result) => {
            if (result.isConfirmed) {
              showHelpModal();
            }
          });
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Your token have been successfully saved!',
          });
          handleCancel();

          let apiResponseCCToken = res.data.companycam_token;
          setcompanyCamToken(apiResponseCCToken);
          fetchCompanycam();
          localStorage.setItem('companycam_token', apiResponseCCToken);
        }
      })
      .catch((err) => {
        console.info('Get Error Edit State...');
      });
  };

  const handleCheckAllChange = (e) => {
    if (e.target.checked) {
      const allCompImgs = companyCamProImg.map((c) => c?.uris[3]?.uri);
      setselectedImages(allCompImgs);
    } else {
      setselectedImages([]);
    }
  };

  async function saveCompanyCamMedia() {
    setuploading(true);
    const values = [];
    const keys = selectedImages;
    const key1 = 'url';
    const key2 = 'media_id';
    const key3 = 'status';
    const numOfimg = selectedImages.length;
    for (let i = 0; i < selectedImages.length; i++) {
      const url = selectedImages[i];

      try {
        const response = await axios.post(
          INSPECTION_URL + 'uploadMediafromCompanycam/',
          { url }
        );
        values[i] = response.data;
        if (numOfimg > 1) {
          let remainingImgs = numOfimg - (i + 1);
          let progressPercent = ((numOfimg - remainingImgs) / numOfimg) * 100;
          setCcprogress(progressPercent);
        } else {
          setCcprogress(0);
        }
      } catch (error) {
        console.error(`Error sending URL ${url} to API: ${error.message}`);
      }
    }

    const result = keys.reduce((acc, key, index) => {
      acc.push({
        [key1]: key,
        [key2]: values[index],
        [key3]: 'done',
        uid: values[index],
      });
      return acc;
    }, []);
    console.warn({ result });
    const newFileList = [...fileList, ...result];
    setFileList(newFileList);
    if (takefileList) {
      takefileList(newFileList);
    }
    if (props.handleFill) {
      props.handleFill({ attachments: newFileList });
    }
    setuploading(false);
    setselectedImages([]);
    setIsModalOpen(false);
    setCcprogress(0);
  }
   function saveFDCamMedia() {
    setuploading(true);
    
    // Process Images
    const imageResult = fDCamImages.map((image) => ({
      url: image.url,
      media_id: image.id,
      status: 'done',
      uid: `${image.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `image_${image?.id}_${Date.now()}`,
      id: image.id,
      isFdcam: true,
      fdcam_project_id: image.fdcam_project_id || fdCamSelectedProject?.id,
      desc: image.description,
      report_id: props.values.report_id || props.values.req_id,
      project_id: props.values.project_id,
      annotations_data: image.annotations_data || null,
      original_image_url: image.original_image_url || image.url,
      original_source_url: image.original_source_url || image.original_image_url || image.url
    }));

    props.values.Allimagedescription = {
      ...props.values.Allimagedescription,
      ...Object.fromEntries(imageResult.map((item) => [item.uid, { caption: item.desc || '' }])),
    }

    setFileList(prevFileList => {
      const newFileList = [...prevFileList, ...imageResult];
      if (takefileList) {
        takefileList(newFileList);
      }
      if (props.handleFill) {
        props.handleFill({ attachments: newFileList });
      }
      return newFileList;
    });

    setuploading(false);
    setFDCamImages([]);
    setIsFDCamModalOpen(false);
    setCcprogress(0);
    if (imageResult.length > 0) {
      setPreviewImage(imageResult[0]);
    }
  }

  function saveFDCamVideos() {
    setuploading(true);
    
    // Sort videos chronologically (first taken/uploaded first)
    const sortedVideos = [...fdCamSelectedVideos].sort((a, b) => {
      const timeA = new Date(a.created_at || a.createdAt || 0).getTime();
      const timeB = new Date(b.created_at || b.createdAt || 0).getTime();
      if (timeA !== timeB && timeA > 0 && timeB > 0) return timeA - timeB;
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    });

    // Process Videos
    const videoResult = sortedVideos.map((video) => ({
      url: video.url,
      media_id: video.id,
      status: 'done',
      uid: `video_${video.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: video.name || `video_${video.id}`,
      thumbUrl: video.thumb_url,
      id: video.id,
      isFdcam: true,
      type: 'video',
      desc: video.description || '',
      created_at: video.created_at || null,
      report_id: props.values.report_id || props.values.req_id,
      project_id: props.values.project_id
    }));

    const newAllImageDescription = {
      ...props.values.Allimagedescription,
      ...Object.fromEntries(videoResult.map((item) => [item.uid, { caption: item.desc || '' }])),
    };

    // Direct mutation for immediate availability (same pattern as images at line 516)
    props.values.Allimagedescription = newAllImageDescription;

    setVideoFileList(prevVideoList => {
      const newVideoList = [...prevVideoList, ...videoResult];
      if (props.handleFill) {
        props.handleFill({ 
          video_attachments: newVideoList,
          Allimagedescription: newAllImageDescription
        });
      }
      return newVideoList;
    });

    setuploading(false);
    setfdCamSelectedVideos([]);
    setIsFDCamModalOpen(false);
    setCcprogress(0);
  }

  const handleButtonClick = () => {
    const externalUrl = 'https://app.companycam.com/access_tokens';
    // Open the URL in a new tab
    window.open(externalUrl, '_blank');
  };

  const fetchCompanycamProImg = (project_id, index) => {
    console.info({ index });
    setActive(index);
    setCamLoader(true);
    dispatch(
      getCompanycamProPhotosService.getCompanycamProjectPhotos(project_id)
    )
      .then((res) => {
        var selectedProject = companyCam.filter(function (el) {
          return el.id === project_id;
        });
        setCompanycamHeading(selectedProject[0]?.name);
        setCompanycamAddressStreet(
          selectedProject[0]?.address['street_address_1']
        );
        setCompanycamAddressCity(selectedProject[0]?.address['city']);
        setCompanycamAddressState(selectedProject[0]?.address['state']);
        setCompanycamAddressPostal(selectedProject[0]?.address['postal_code']);

        // To sort images based on ID
        const sortedImages = res.sort((a, b) => a.id - b.id);
        setCompanycamProImg(sortedImages);

        setCamLoader(false);
      })
      .catch((err) => {
        console.info('Get Error Edit State...');
      });
  };

  const hadleChangeCompanycam = (e) => {
    let companyCamimgs = [];
    let url = e.target.value;
    companyCamimgs = [...selectedImages];
    if (companyCamimgs?.includes(url)) {
      let deselectedUrl = companyCamimgs.indexOf(url);
      delete companyCamimgs[deselectedUrl];
    } else {
      companyCamimgs.push(url);
    }
    companyCamimgs = companyCamimgs?.filter((e) => e);
    setselectedImages(companyCamimgs);
  };
  useEffect(() => {
    const attachments = props.values.attachments ? props.values.attachments : [];
    setFileList(attachments);

    const videoAttachments = props.values.video_attachments ? props.values.video_attachments : [];
    setVideoFileList(videoAttachments);

    if (attachments.length > 0) {
      // Check if current cover_image is valid and exists in current attachments
      const currentCoverValid = props.values.cover_image && attachments.some(f => 
        (f.media_id && f.media_id == props.values.cover_image) || 
        (f.uid && f.uid == props.values.cover_image)
      );

      // If no valid cover image is selected (either new report or current selection deleted), select one by default
      if (!currentCoverValid) {
        // Check if an old report already has a cover indicator on a file object
        const existingCover = attachments.find(f => 
          f.cover && (f.cover == f.media_id || f.cover == f.uid || (f.media_id && parseInt(f.cover) === f.media_id))
        );
        
        let targetCoverId = '';
        if (existingCover) {
          targetCoverId = existingCover.media_id || existingCover.uid;
        } else {
          // Default to the first image in the list if no previous selection exists
          targetCoverId = attachments[0].media_id || attachments[0].uid;
        }

        if (targetCoverId) {
          if (props.handleFill) {
            props.handleFill({ cover_image: targetCoverId });
          } else {
            // Fallback for cases where handleFill might not be available
            props.values.cover_image = targetCoverId;
          }
        }
      }
    }

    let localCCTokenItem = localStorage.getItem('companycam_token');
    if (localCCTokenItem) {
      setcompanyCamToken(localCCTokenItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.values.attachments, props.values.video_attachments]);

  // Clean up selection when fileList changes
  useEffect(() => {
    if (fileList.length < 2 && isReorderMode) {
      // Auto-exit reorder mode if less than 2 images
      setIsReorderMode(false);
      setSelectedForDrag([]);
    } else if (selectedForDrag.length > 0) {
      // Remove selected items that no longer exist in fileList
      const validSelection = selectedForDrag.filter(uid => 
        fileList.some(file => file.uid === uid)
      );
      if (validSelection.length !== selectedForDrag.length) {
        setSelectedForDrag(validSelection);
      }
    }
  }, [fileList, isReorderMode, selectedForDrag]);


const beforeUpload = (file) => {
  const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
  
  const isJpgOrPngOrHeic =
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/gif' ||
    file.type === 'image/jpg' ||
    isHeic;

  if (!isJpgOrPngOrHeic) {
    message.error('You can only upload JPG/PNG/GIF/HEIC files!');
    return Upload.LIST_IGNORE;
  }

  return true;
};

const uploadImage = async (options) => {
  let { onSuccess, onError, file, onProgress } = options;
  const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
  
  // Logic to handle HEIC conversion in Parallel
  if (isHeic) {
    activeHeicConversionCount++;
    message.loading({ 
      content: `Converting HEIC files... (${activeHeicConversionCount} remaining)`, 
      key: conversionMessageKey, 
      duration: 0 
    });

    try {
      const blobResult = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.8
      });

      // Handle both single blob and array responses from heic2any
      const finalBlob = Array.isArray(blobResult) ? blobResult[0] : blobResult;

      file = new File(
        [finalBlob],
        file.name.replace(/\.[^/.]+$/, "") + ".jpg",
        { type: 'image/jpeg' }
      );
    } catch (error) {
      console.error('HEIC conversion failed for:', file.name, error);
      message.error(`Failed to convert ${file.name}`);
    } finally {
      activeHeicConversionCount--;
      if (activeHeicConversionCount > 0) {
        message.loading({ 
          content: `Converting HEIC files... (${activeHeicConversionCount} remaining)`, 
          key: conversionMessageKey, 
          duration: 0 
        });
      } else {
        message.success({ content: 'All HEIC files converted!', key: conversionMessageKey, duration: 2 });
      }
    }
  }

  const fmData = new FormData();
  const config = {
    headers: {
      'content-type': 'multipart/form-data',
      Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
    },
    onUploadProgress: (event) => {
      onProgress({ percent: (event.loaded / event.total) * 100 });
    },
  };
  fmData.append('file', file);
  
  // Laravel accepts either report_id OR project_id, not both
  const reportId = props.values.report_id || props.values.req_id;
  if (reportId) {
    fmData.append('report_id', reportId);
  } else if (props.values.project_id) {
    fmData.append('project_id', props.values.project_id);
  }

  try {
    const res = await axios.post(MEDIA_UPLOAD_URL_IMAGE, fmData, config);
    onSuccess(res.data);
  } catch (err) {
    console.error('Upload Error:', err);
    onError({ err });
  }
};

  const onChange = (info) => {
    const newFileList = info.fileList.map(file => {
      // Laravel returns flat JSON: { id, source_url, thumb_url, ... }
      // onSuccess(res.data) means file.response = that flat object directly
      if (file.status === 'done' && file.response && file.response.source_url) {
        file.url = file.response.source_url;
        file.media_id = file.response.id;
        file.uid = file.response.id;
        file.isLaravel = true;
        file.thumbUrl = file.response.thumb_url || file.response.source_url;
        file.report_id = props.values.report_id || props.values.req_id;
        file.project_id = props.values.project_id;
      }
      return file;
    });
    setFileList(newFileList);
    
    // Call takefileList if it exists (usually for local state management in some parent wrappers)
    if (takefileList) {
      takefileList(newFileList);
    }

    // CRITICAL: Call handleFill to ensure the deletion/addition is persisted to the report state
    if (props.handleFill) {
      props.handleFill({ attachments: newFileList });
    }
  };

  const onVideoChange = (info) => {
    const newVideoList = info.fileList.map(file => {
      // Laravel returns flat JSON: { id, source_url, thumb_url, ... }
      // onSuccess(res.data) means file.response = that flat object directly
      if (file.status === 'done' && file.response && file.response.source_url) {
        file.url = file.response.source_url;
        file.media_id = file.response.id;
        file.uid = file.response.id;
        file.type = 'video';
        file.thumbUrl = file.response.thumb_url || file.response.source_url;
        file.isLaravel = true;
        file.report_id = props.values.report_id || props.values.req_id;
        file.project_id = props.values.project_id;
      }
      return file;
    });
    setVideoFileList(newVideoList);
    if (props.handleFill) {
      props.handleFill({ video_attachments: newVideoList });
    }

    // Show message when video is deleted
    if (info.file.status === 'removed') {
      message.success({
        content: `Video deleted successfully! 🗑️`,
        duration: 3,
        style: { marginTop: '20px' },
      });
    }
  };

  const uploadVideo = async (options) => {
    const { onSuccess, onError, file, onProgress } = options;
    const fmData = new FormData();
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
      },
      onUploadProgress: (event) => {
        onProgress({ percent: (event.loaded / event.total) * 100 });
      },
    };
    fmData.append('file', file);
    // Laravel accepts either report_id OR project_id, not both
    const reportId = props.values.report_id || props.values.req_id;
    if (reportId) {
      fmData.append('report_id', reportId);
    } else if (props.values.project_id) {
      fmData.append('project_id', props.values.project_id);
    }

    try {
      const res = await axios.post(MEDIA_UPLOAD_URL_VIDEO, fmData, config);
      onSuccess(res.data);
      message.success({
        content: `${file.name || 'Video'} uploaded successfully! 🎬`,
        duration: 3,
        style: { marginTop: '20px' },
      });
    } catch (err) {
      console.log('Video Error: ', err);
      onError({ err });
      message.error({
        content: `${file.name || 'Video'} upload failed. Please try again.`,
        duration: 4,
        style: { marginTop: '20px' },
      });
    }
  };
  const onChangeCover = (e) => {
    if (props.handleFill) {
      props.handleFill({ cover_image: e.target.value });
    } else {
      props.values.cover_image = e.target.value;
    }

  };
    const handlePreview = async (file) => {
      setLoading(true);

      try {
        let parsedAnnotations = file.annotations_data;
        if (typeof parsedAnnotations === 'string') {
          try {
            parsedAnnotations = JSON.parse(parsedAnnotations);
          } catch (e) {}
        }
        const hasAnnotations = parsedAnnotations && (
          (Array.isArray(parsedAnnotations) && parsedAnnotations.length > 0) ||
          (Array.isArray(parsedAnnotations.objects) && parsedAnnotations.objects.length > 0) ||
          (parsedAnnotations.objects && typeof parsedAnnotations.objects === 'object' && Object.keys(parsedAnnotations.objects).length > 0)
        );

        let rawClean = file.original_source_url || file.original_image_url;
        let editorSource = (hasAnnotations && rawClean && rawClean !== file.url)
          ? rawClean
          : file.url;

        if (!hasAnnotations) {
          if (file.url?.startsWith("http") && !file.url?.startsWith("blob:")) {
            try {
              const sep = file.url.includes("?") ? "&" : "?";
              const response = await fetch(`${file.url}${sep}cacheBust=${Date.now()}`);
              if (response.ok) {
                const blob = await response.blob();
                editorSource = URL.createObjectURL(blob);
              }
            } catch (err) {
              console.warn("Could not fetch image blob for editing, falling back to direct URL:", err);
              editorSource = file.url;
            }
          } else if (file.originFileObj) {
            editorSource = URL.createObjectURL(file.originFileObj);
          }
        } else if (editorSource && !editorSource.startsWith("blob:")) {
          const sep = editorSource.includes("?") ? "&" : "?";
          editorSource = `${editorSource}${sep}cb=${Date.now()}`;
        }

        setPreviewImage({
          ...file,
          annotations_data: hasAnnotations ? parsedAnnotations : null,
          editorSource: editorSource || file.url
        });
        setIsImgEditorShown(true);
        setPreviewVisible(true);

        setPreviewTitle(
          file.name || (file.url && file.url.substring(file.url.lastIndexOf("/") + 1))
        );
      } catch (error) {
        console.error("Failed to prepare image for editing:", error.message);
        message.error("Could not load image for editing.");
      } finally {
        setLoading(false);
      }
    };


  const showModal = () => {
    fetchCompanycam();
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancelModal = () => {
    setIsModalOpen(false);
  };
  const showTokenModal = () => {
    setIsModalToken(true);
  };
  const handleOkToken = () => {
    setIsModalToken(false);
  };
  const handleCancelToken = () => {
    setIsModalToken(false);
  };
  const showHelpModal = () => {
    setIsModalHelp(true);
  };
  const handleOkHelp = () => {
    setIsModalHelp(false);
  };
  const handleCancelHelp = () => {
    setIsModalHelp(false);
  };

  const fetchProjects = (pageNum, search = '') => {
    if (!hasMore || loading) return;

    setLoading(true);

    const params = { page: pageNum, search };

    dispatch(getProjects(params))
      .then((response) => {
        const newProjects = (response?.data || []).filter(
          p => !p.is_archived && !p.archived_at
        );

        // If no new projects returned, we've reached the end
        if (newProjects.length === 0) {
          setHasMore(false);
        } else {
          // Append new projects to existing ones
          setProjects(prev => [...prev, ...newProjects]);
          setPage(pageNum);
        }
        
      })
      .catch((error) => {
        message.error('Failed to fetch projects');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const searchFdProjects = (pageNum,search) =>{
    const params = { page: pageNum, search };
    dispatch(getProjects(params))
    .then((response) => {
      const newProjects = (response?.data || []).filter(
        p => !p.is_archived && !p.archived_at
      );

      // Append new projects to existing ones
      setProjects(newProjects);
      setPage(pageNum);
    })
    .catch((error) => {
      message.error('Failed to fetch projects');
    })
    .finally(() => {
      setLoading(false);
    });
  }

  const handleScroll = () => {
    if (!sidebarRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = sidebarRef.current;

    // Check if we're near the bottom (within 50px)
    if (scrollHeight - scrollTop - clientHeight < 50 && !loading && hasMore) {
      fetchProjects(page + 1);
    }
  };

  const fd_backend_token = localStorage.getItem('fd_backend_token') 

  useEffect(() => {
    
    if (fd_backend_token) {
      setHideFdcamButton(false);
      fetchProjects(1);
    }
  }, [fd_backend_token]);

  const showFDCamModal = (type = 'photos') => {
    setFdCamModalType(type);
    setFdCamView(type);
    setfdCamSelectedProjects(null); // Clear selected project to allow new selection
    setIsImgEditorShown(true)
    setIsFDCamModalOpen(true);
  };
  const handleFDCamOk = () => {
    setIsFDCamModalOpen(false);

  };
  const handleCancelFDCamModal = () => {
    setIsFDCamModalOpen(false);
  };
  const handleFdCamCheck = (value, fdCamProject) => {
    if (value?.target?.checked === true) {
      setSelectImagesFDCam(true);
      setFDCamImages(fdCamProject?.inspection_images?.map((item) => ({
        id: item?.id,
        media_id: item?.id,
        uid: `${item.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: `image_${item?.id}_${Date.now()}`,
        url: item?.image_url,
        description: item?.description,
        annotations_data: item?.annotations_data || null,
        original_image_url: item?.original_image_url || item?.original_image_path || item?.image_url,
        original_source_url: item?.original_image_url || item?.original_image_path || item?.image_url,
        fdcam_project_id: fdCamProject?.id,
        isFdcam: true
      })));
    } else {
      setSelectImagesFDCam(false);
      setFDCamImages([]);
    }
  };
    const handleImageCheck = (imageId) => {
    const projectImage = fdCamSelectedProject.inspection_images.find(img => img.id === imageId);
    if (!projectImage) return;

    const newSelectedImages = fDCamImages.some(image => image.id === imageId)
      ? fDCamImages.filter(image => image.id !== imageId)
      : [...fDCamImages, {
        id: imageId,
        media_id: imageId,
        url: projectImage.image_url,
        description: projectImage.description,
        annotations_data: projectImage?.annotations_data || null,
        original_image_url: projectImage?.original_image_url || projectImage?.original_image_path || projectImage?.image_url,
        original_source_url: projectImage?.original_image_url || projectImage?.original_image_path || projectImage?.image_url,
        fdcam_project_id: fdCamSelectedProject?.id,
        isFdcam: true
      }];

    setFDCamImages(newSelectedImages);

    // Update the "Select All" checkbox state
    if (newSelectedImages.length === fdCamSelectedProject?.inspection_images?.length) {
      setSelectImagesFDCam(true);
    } else {
      setSelectImagesFDCam(false);
    }
  };

  const handleVideoCheck = (videoId) => {
    const video = fdCamSelectedProject?.inspection_videos?.find(v => v.id === videoId);
    if (!video) return;

    const newSelectedVideos = fdCamSelectedVideos.some(v => v.id === videoId)
      ? fdCamSelectedVideos.filter(v => v.id !== videoId)
      : [...fdCamSelectedVideos, {
        id: videoId,
        url: video.source_url,
        thumb_url: video.thumb_url,
        name: video.original_filename,
        description: video.description,
        created_at: video.created_at || null
      }];

    setfdCamSelectedVideos(newSelectedVideos);

    // Update the "Select All" checkbox for videos
    if (newSelectedVideos.length === fdCamSelectedProject?.inspection_videos?.length) {
      setSelectVideosFDCam(true);
    } else {
      setSelectVideosFDCam(false);
    }
  };

  const handleVideoSelectAll = (e, fdCamProject) => {
    if (e.target.checked) {
      setSelectVideosFDCam(true);
      setfdCamSelectedVideos(fdCamProject?.inspection_videos?.map(video => ({
        id: video.id,
        url: video.source_url,
        thumb_url: video.thumb_url,
        name: video.original_filename,
        description: video.description,
        created_at: video.created_at || null
      })));
    } else {
      setSelectVideosFDCam(false);
      setfdCamSelectedVideos([]);
    }
  };

  const handleSelectFdCamSelect = (projectitem) => {
    setFdCamLoader(true)
    setfdCamSelectedProjects(projectitem);
    setFDCamImages([]);
    setfdCamSelectedVideos([]);
    setSelectImagesFDCam(false);
    setSelectVideosFDCam(false);
    setFdCamView('photos');
    setFdCamLoader(false)
  }

  const handleSearch = (value) => {
    const searchVal = value.trim().toLowerCase();
    searchFdProjects(1, searchVal);
  };

    function dataURLtoBlob(dataURL) {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

 const closeImgEditor = () => {
    if (previewImage?.editorSource && previewImage.editorSource.startsWith("blob:") && previewImage.editorSource !== previewImage.url) {
      try {
        URL.revokeObjectURL(previewImage.editorSource);
      } catch (e) {}
    }
    setPreviewImage('');
    setIsImgEditorShown(false);
    setPreviewVisible(false);
  };

    const handleSave = async (editedImage) => {
    try {
      const blob = dataURLtoBlob(editedImage.imageBase64);
      const file = new File([blob], editedImage.fullName, {
        type: editedImage.mimeType,
      });

      const formData = new FormData();
      formData.append('file', file);

      let uploadedUrl = '';
      let newMediaId = '';
      let newThumbUrl = '';
      let uploadedOriginalUrl = '';

      const targetProjectId = previewImage?.fdcam_project_id || previewImage?.project_id || props.values?.project_id;
      const targetImageId = previewImage?.media_id || previewImage?.id;

      if (previewImage?.isFdcam && targetProjectId && targetImageId) {
        console.warn({previewImage, targetProjectId, targetImageId});
        // If it's an FDCam image — update the original FDCam project image
        const updateFormData = new FormData();
        updateFormData.append('image', file);
        updateFormData.append('project_id', targetProjectId);
        updateFormData.append('_method', 'PATCH');
        if (editedImage.cleanBase64) {
          const cleanBlob = dataURLtoBlob(editedImage.cleanBase64);
          const cleanFile = new File([cleanBlob], `clean-${editedImage.fullName}`, { type: editedImage.mimeType });
          updateFormData.append('clean_image', cleanFile);
        }
        if (editedImage.annotations) {
          updateFormData.append('annotations_data', JSON.stringify(editedImage.annotations));
        }
        const uploadResponse = await dispatch(updateProjectImage(targetProjectId, targetImageId, updateFormData));

        uploadedUrl = uploadResponse?.image_url;
        newMediaId = uploadResponse?.id || targetImageId;
        newThumbUrl = uploadResponse?.thumb_url;
        uploadedOriginalUrl = uploadResponse?.original_image_url;
      } else {
        // For normal uploads — use axios to upload as a new inspection media to Laravel
        if (props.values.req_id || props.values.report_id) {
          formData.append('report_id', props.values.report_id || props.values.req_id);
        }
        if (props.values.project_id) {
          formData.append('project_id', props.values.project_id);
        }
        if (editedImage.annotations) {
          formData.append('annotations_data', JSON.stringify(editedImage.annotations));
        }
        const uploadResponse = await axios.post(MEDIA_UPLOAD_URL_IMAGE, formData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
          },
        });

        if (uploadResponse.status !== 201) {
          throw new Error('Upload failed');
        }

        uploadedUrl = uploadResponse.data?.source_url;
        newMediaId = uploadResponse.data?.id;
        newThumbUrl = uploadResponse.data?.thumb_url;
        uploadedOriginalUrl = uploadResponse.data?.original_url || '';
      }

      // Immediately update preview and modal UI
      setPreviewImage('');
      setIsFDCamModalOpen(false);

      // Keep track of the old UID and its description text to transfer to the new UID
      const oldUid = previewImage?.uid;
      const oldUidStr = oldUid?.toString();
      const newUidStr = newMediaId?.toString();
      const oldDescText = props.values.Allimagedescription?.[oldUidStr]?.caption;

      // Check if this image was selected as the cover image (either in form values or via database fields)
      const isCover =
        (props.values.cover_image && (
          props.values.cover_image.toString() === oldUidStr ||
          (previewImage?.media_id && props.values.cover_image.toString() === previewImage.media_id.toString())
        )) ||
        (!props.values.cover_image && previewImage?.cover && (
          previewImage.cover.toString() === oldUidStr ||
          (previewImage?.media_id && previewImage.cover.toString() === previewImage.media_id.toString())
        ));

      if (oldUidStr && newUidStr && oldUidStr !== newUidStr) {
        if (oldDescText) {
          const newAllImageDescription = {
            ...(props.values.Allimagedescription || {}),
            [newUidStr]: {
              caption: oldDescText
            },
          };
          props.values.Allimagedescription = newAllImageDescription;
          if (props.handleFill) {
            props.handleFill({ Allimagedescription: newAllImageDescription });
          }

          // Also save/patch the description on Laravel for the new media item
          if (newMediaId && !isNaN(newMediaId)) {
            axios.patch(`${MEDIA_UPLOAD_URL_IMAGE}/${newMediaId}`, {
              description: oldDescText
            }, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
              }
            }).catch((error) => {
              console.error("Failed to autosave description for edited media:", error);
            });
          }
        }

        if (isCover) {
          if (props.handleFill) {
            props.handleFill({ cover_image: newMediaId });
          } else {
            props.values.cover_image = newMediaId;
          }
        }
      }

      // Update file list
      const isThisFdcam = Boolean(previewImage?.isFdcam);
      const cacheBustedUrl = uploadedUrl ? `${uploadedUrl.split('?')[0]}?t=${Date.now()}` : '';
      const rawThumb = newThumbUrl || '';
      const cacheBustedThumb = rawThumb.split('?')[0] ? `${rawThumb.split('?')[0]}?t=${Date.now()}` : '';

      const updatedList = fileList.map((file) => {
        const currFileId = file.media_id || file.uid;
        const previewFileId = previewImage?.media_id || previewImage?.uid;

        // If the edited image was the cover, update the cover property of all items in the list to the new media ID
        const updatedCover = isCover ? newMediaId : file.cover;

        const rawResolved = (uploadedOriginalUrl && uploadedOriginalUrl !== uploadedUrl)
          ? uploadedOriginalUrl
          : ((file.original_source_url && file.original_source_url !== file.url)
              ? file.original_source_url
              : ((file.original_image_url && file.original_image_url !== file.url)
                  ? file.original_image_url
                  : (previewImage?.editorSource && !previewImage.editorSource.startsWith('blob:') && previewImage.editorSource !== file.url
                      ? previewImage.editorSource
                      : (file.original_source_url || file.original_image_url || file.url))));
        const resolvedOriginal = rawResolved;

        return currFileId === previewFileId
          ? {
              ...file,
              url: cacheBustedUrl || file.url,
              thumbUrl: cacheBustedThumb || cacheBustedUrl || file.thumbUrl || file.url,
              originFileObj: blob,
              media_id: newMediaId || file.media_id,
              uid: file.uid || newMediaId,
              desc: oldDescText || file.desc,
              cover: updatedCover,
              status: 'done',
              isLaravel: isThisFdcam ? false : true,
              isFdcam: isThisFdcam,
              fdcam_project_id: targetProjectId || previewImage?.fdcam_project_id || file.fdcam_project_id,
              annotations_data: editedImage.annotations,
              original_source_url: resolvedOriginal,
              original_image_url: resolvedOriginal,
            }
          : {
              ...file,
              cover: updatedCover,
            };
      });

      setFileList(updatedList);
      if (props.values) {
        props.values.attachments = updatedList;
      }
      if (takefileList) {
        takefileList(updatedList);
      }
      if (props.handleFill) {
        props.handleFill({ attachments: updatedList });
      }
      searchFdProjects(1, '');

      // For FDCam, also update in-memory project images
      if (isThisFdcam) {
        setfdCamSelectedProjects((prevProject) => {
          if (!prevProject || !prevProject.inspection_images) return prevProject;

          const updatedImages = prevProject.inspection_images.map((img) =>
            (img.id === previewImage.media_id || img.id === previewImage.id)
              ? {
                  ...img,
                  image_url: cacheBustedUrl || img.image_url,
                  annotations_data: editedImage.annotations,
                  original_image_url: uploadedOriginalUrl || img.original_image_url,
                  updated_at: new Date().toISOString(),
                }
              : img
          );

          return {
            ...prevProject,
            inspection_images: updatedImages,
          };
        });

        message.success('Image updated successfully!');
      }

    } catch (error) {
      console.error('Upload failed:', error);
      message.error('Failed to upload image');
    } finally {
      setIsImgEditorShown(false);
      setPreviewVisible(false);
    }
  };


  return (
    <>
      <Col span={24} className="p-0">
      {/* Reorder Controls (Sticky) */}
      {fileList.length >= 2 && (
        <div className='reorder-controls-sticky'>
          <div className='reorder-controls-content'>
            <Button 
              className='d-flex align-items-center gap-1 submitBtn'
              type='default'
              onClick={toggleReorderMode}
            >
              <MdSwapCalls /> Rearrange Photos
            </Button>
            {isReorderMode && selectedForDrag.length > 0 && (
              <>
                <Button 
                  className='d-flex align-items-center gap-1 btn-outline-danger'
                  type='default'
                  onClick={() => setSelectedForDrag([])}
                >
                  <ReloadOutlined /> Clear Selection
                </Button>
                <span style={{ marginLeft: '10px', color: '#4caf50', fontWeight: '500' }}>
                  {selectedForDrag.length} image(s) selected
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Photos Section */}
      <div className="photos-upload-block mb-5">
        <div className="attachmentHead mb-3">
          <h4 className="formGroupTitle">Add Attachments (Photos)</h4>
        </div>
        
        <DndProvider backend={HTML5Backend}>
          <CustomDragLayer />
          <AutoScrollManager />
          <Form.Item
            name="dragger"
            valuePropName={fileList}
            getValueFromEvent={normFile}
            noStyle
          >
            <Upload
              className="premium-uploader"
              name="avatar"
              multiple={true}
              accept="image/*,.heic,.HEIC,.heif,.HEIF"
              beforeUpload={beforeUpload}
              customRequest={uploadImage}
              listType="picture-card"
              fileList={fileList}
              onChange={onChange}
              onPreview={handlePreview}
              progress={progress}
              itemRender={(originNode, file, currFileList) => (
                <div className="uploaded-img-wrapper image_attachment_uploader">
                  <div className="img-section">
                    <Fancybox>
                      <a
                        data-fancybox="gallery"
                        href={file.url}
                        className="zIndex"
                        data-type="image"
                      >
                        <EyeOutlined />
                      </a>
                    </Fancybox>
                    <DragableUploadListItem
                      originNode={originNode}
                      file={file}
                      fileList={currFileList}
                      moveRow={moveRow}
                      isReorderMode={isReorderMode}
                      selectedForDrag={selectedForDrag}
                      onSelectForDrag={handleSelectForDrag}
                    />
                    <div className="upload-img-number">
                      <span className="img-number">
                        {currFileList.indexOf(file) + 1}
                      </span>
                    </div>
                  </div>

                  <div className="img-text-wrapper">
                    <div className="input-header">
                      <Title level={5}>Description</Title>
                    </div>
                    <Form.Item
                      name={['Allimagedescription', file.uid.toString(), 'caption']}
                      initialValue={props.values.Allimagedescription?.[file.uid.toString()]?.caption || file.desc}
                    >
                      <Input.TextArea
                        className="input-img"
                        onChange={(e) => handleDescriptionChange(file.uid.toString(), e.target.value)}
                      />
                    </Form.Item>
                  </div>
                  
                  <div className="img-footer">
                    <div className="cover-image-radio-wrapper">
                      <Radio.Group
                        value={
                          (props.values.cover_image && props.values.cover_image == (file.media_id || file.uid)) ||
                          (!props.values.cover_image && file.cover && (file.cover == file.media_id || parseInt(file.cover) === file.media_id))
                            ? (file.media_id || file.uid)
                            : ''
                        }
                        onChange={onChangeCover}
                      >
                        <Radio name="cover_image" value={file?.media_id || file?.uid}>
                          <p>Please Select Cover Photo</p>
                        </Radio>
                      </Radio.Group>
                    </div>
                  </div>
                </div>
              )}
            >
              <p className="file-image-drag-btn">Drop or Upload Photos Only Here (Videos Not Supported)</p>
            </Upload>
          </Form.Item>
        </DndProvider>

        <Row gutter={16} justify="center" className="mt-4">
          <Col>
            <div className="CompanySection">
              <Button className="btnPrimary2 d-flex align-items-center" onClick={showModal}>
                <img
                  src="https://d33v4339jhl8k0.cloudfront.net/docs/assets/5e94d6a504286364bc984894/images/630d36a64cde766bbe140183/Full-Color-Over-White.png"
                  alt="companycam-logo"
                />
              </Button>
            </div>
          </Col>
          {!hideFdcamButton && (
            <Col>
              <div className="fdAppbuttonSection">
                <Button className="btnPrimary2 d-flex align-items-center gap-2" onClick={() => showFDCamModal('photos')}>
                  <img
                    className="fd-cam-image"
                    src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2021/08/21062258/no-image-165fc0a7fd134865fc0a7fd1f97.jpg"
                    alt="fd-logo"
                    style={{ height: '24px', width: '24px', borderRadius: '4px' }}
                  />
                  <b>Photo App</b>
                </Button>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* Video Attachment Section */}
      <div className="video-upload-block mt-5 pt-4 border-top">
        <div className="attachmentHead mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="formGroupTitle m-0">Add Video Attachments</h4>
          {hasVideoOrderChanged && (
            <div className="d-flex align-items-center gap-2 video-reorder-actions">
              <Button
                type="primary"
                className="btn-save-video-order"
                icon={<CheckOutlined />}
                loading={savingVideoOrder}
                onClick={handleSaveVideoOrder}
              >
                Save Video Order
              </Button>
              <Button
                type="primary"
                danger
                className="btn-cancel-video-order"
                icon={<LuRefreshCcw />}
                disabled={savingVideoOrder}
                onClick={handleCancelVideoOrder}
              >
                Reset Order
              </Button>
            </div>
          )}
        </div>
        
        <div className="p-3 bg-light rounded border">
          <DndContext
            sensors={videoSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleVideoDragEnd}
          >
            <SortableContext
              items={videoFileList.map(v => String(v.uid || v.id || v.media_id))}
              strategy={rectSortingStrategy}
            >
              <Form.Item
                name="video_dragger"
                valuePropName={videoFileList}
                getValueFromEvent={normFile}
                noStyle
              >
                <Upload
                  className="premium-uploader"
                  name="videoFiles"
                  multiple={true}
                  accept="video/*"
                  customRequest={uploadVideo}
                  listType="picture-card"
                  fileList={videoFileList}
                  onChange={onVideoChange}
                  progress={progress}
                  itemRender={(originNode, file, currFileList) => (
                    <SortableVideoUploadItem file={file}>
                      {({ attributes, listeners }) => (
                        <div className="uploaded-img-wrapper image_attachment_uploader video-card-wrapper">
                          <div className="img-section">
                            {currFileList && currFileList.length >= 2 && file.status !== 'uploading' && (
                              <div
                                className="video-corner-drag-handle"
                                {...attributes}
                                {...listeners}
                                title="Click and drag to rearrange video order"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DragOutlined style={{ fontSize: '15px', color: '#ffffff' }} />
                              </div>
                            )}

                            <div className="video-custom-card-container">
                              {file.status === 'uploading' ? (
                                <div className="video-uploading-placeholder">
                                  <Progress 
                                    type="circle" 
                                    percent={Math.round(file.percent)} 
                                    width={80} 
                                    strokeColor="#52c41a"
                                  />
                                  <div className="mt-2" style={{ color: '#52c41a', fontWeight: 'bold' }}>Uploading...</div>
                                </div>
                              ) : (
                                <div className="video-thumb-wrapper">
                                  <video 
                                    src={`${file.url}#t=0.001`} 
                                    poster={file.thumbUrl || file.thumb_url || file.thumbnail || file.thumbnail_url} 
                                    preload="metadata"
                                    muted
                                    className="video-thumb-img"
                                  />
                                  <div className="video-overlay-actions">
                                    <div 
                                      className="video-action-btn play-btn"
                                      title="Play Video"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const allVideoItems = videoFileList.map(v => ({
                                          src: v.url,
                                          type: 'html5video',
                                          caption: props.values.Allimagedescription?.[v.uid?.toString()]?.caption || v.desc || ''
                                        }));
                                        const currentIndex = videoFileList.findIndex(v => v.uid === file.uid);
                                        NativeFancybox.show(allVideoItems, {
                                          startIndex: currentIndex >= 0 ? currentIndex : 0
                                        });
                                      }}
                                    >
                                      <PlayCircleOutlined />
                                    </div>
                                    <div 
                                      className="video-action-btn delete-btn" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onVideoChange({
                                          file: { ...file, status: 'removed' },
                                          fileList: videoFileList.filter(v => v.uid !== file.uid)
                                        });
                                      }}
                                      title="Delete Video"
                                    >
                                      <DeleteOutlined />
                                    </div>
                                  </div>
                                  <div className="video-index-badge">
                                    {currFileList.indexOf(file) + 1}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="img-text-wrapper" onClick={(e) => e.stopPropagation()}>
                            <div className="input-header">
                              <Title level={5}>Video Description</Title>
                            </div>
                            <Form.Item
                              name={['Allimagedescription', file.uid.toString(), 'caption']}
                              initialValue={props.values.Allimagedescription?.[file.uid.toString()]?.caption || file.desc || ''}
                            >
                              <Input.TextArea
                                className="input-img"
                                onChange={(e) => handleDescriptionChange(file.uid.toString(), e.target.value)}
                              />
                            </Form.Item>
                          </div>
                        </div>
                      )}
                    </SortableVideoUploadItem>
                  )}
                >
                  <p className="file-image-drag-btn">Drop or Upload video Only Here</p>
                </Upload>
              </Form.Item>
            </SortableContext>
          </DndContext>
          
          {!hideFdcamButton && (
            <Row justify="center" className="mt-4">
              <Col>
                <div className="fdAppbuttonSection">
                  <Button className="btnPrimary2 d-flex align-items-center gap-2" onClick={() => showFDCamModal('videos')}>
                    <img
                      className="fd-cam-image"
                      src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2021/08/21062258/no-image-165fc0a7fd134865fc0a7fd1f97.jpg"
                      alt="fd-logo"
                      style={{ height: '24px', width: '24px', borderRadius: '4px' }}
                    />
                    <b>Photo App Videos</b>
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </Col>

      <Modal
        className="editor_rotate_Model"
        open={previewVisible}
        title={null}
        closable={false}
        footer={false}
        onCancel={handleCancel}
        bodyStyle={{ height: "100vh", padding: 0, margin: 0, overflow: "hidden" }}
        width="100vw"
        style={{ top: 0, padding: 0, margin: 0, maxWidth: "100vw", height: "100vh" }}
      >
       {loading && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(255, 255, 255, 0.6)', // optional: light overlay
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999, // ensure it's on top
              }}
            >
              <Spin
                spinning={loading}
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              />
            </div>
          )}

            {isImgEditorShown && (
              <FdCamEditor
                source={previewImage?.editorSource || previewImage?.url}
                initialAnnotations={previewImage?.annotations_data || null}
                fileName={`inspection-image-${previewImage?.media_id || previewImage?.uid || 'edited'}.png`}
                onSave={handleSave}
                onClose={closeImgEditor}
              />
            )}
      </Modal>

      <Modal
        centered
        open={isModalOpen}
        className="inspectionCategoryModal industryModal customIndustryModal companyCamModel"
        width={1000}
        footer={null}
        onOk={handleOk}
        onCancel={handleCancelModal}
        closable={false}
      >
        <Row>
          <Col xs={24} md={24}>
            <div className="modal-header">
              <div className="modal-header-title">
                <Title level={4}>CompanyCam's Projects</Title>
              </div>
              <div className="modal-header-logo model-logo-center">
                <div className="modal-header-edit-logo">
                  <Tooltip title="Edit Your Companycam Token" arrow>
                    <Button className="btnPrimary2" onClick={showTokenModal}>
                      <img
                        src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/04/24023652/cog-regular-36.png"
                        alt="edit-logo"
                      ></img>
                    </Button>
                  </Tooltip>
                </div>
                <Image
                  src="https://d33v4339jhl8k0.cloudfront.net/docs/assets/5e94d6a504286364bc984894/images/630d36a64cde766bbe140183/Full-Color-Over-White.png"
                  className="img-fluid"
                  alt="logos"
                  preview={false}
                />
              </div>
            </div>
          </Col>
        </Row>
        <Form
          name="companyCamform"
          layout="vertical"
          autoComplete="off"
          form={form1}
          onFinish={saveCompanyCamMedia}
        >
          <div>
            <Row className="my-2 footerRow" gutter={6}>
              <Col className="col-4">
                <Row className="my-2 footerRow" gutter={6}>
                  {Object.keys(companyCam).map((keyName, index) => (
                    <Col className="col-12 h-100 mb-2" key={index}>
                      {/* className="companyCam-project-box" */}
                      <a
                        href="#:"
                        className={
                          active === index
                            ? ' companyCam-project-box activeCard'
                            : 'companyCam-project-box'
                        }
                        onClick={() =>
                          fetchCompanycamProImg(companyCam[keyName]?.id, index)
                        }
                      >
                        <div className="logoDiv companyCam-project-outer">
                          <div className="companyCam-pro-thumb">
                            <Image
                              src={companyCam[keyName]?.feature_image[0]?.uri}
                              className="img-fluid"
                              alt="logos"
                              preview={false}
                            />
                          </div>
                          <div className="company-project-details px-2">
                            <h6>
                              <span>
                                <b>Project </b>:-
                              </span>
                              <span className="company-project-title">
                                {companyCam[keyName]?.name}
                              </span>
                            </h6>
                            <p>
                              <span>
                                <b>Address </b>:-
                              </span>
                              <span>
                                {
                                  companyCam[keyName]?.address[
                                  'street_address_1'
                                  ]
                                }
                              </span>
                              ,{' '}
                              <span>
                                {companyCam[keyName]?.address['city']}
                              </span>
                              ,{' '}
                              <span>
                                {companyCam[keyName]?.address['state']}
                              </span>
                              ,{' '}
                              <span>
                                {companyCam[keyName]?.address['postal_code']}
                              </span>
                            </p>
                            <p>
                              <span>
                                <b>Photos </b>:-
                              </span>
                              {companyCam[keyName]?.photo_count}
                            </p>
                          </div>
                        </div>
                      </a>
                    </Col>
                  ))}
                </Row>
              </Col>
              <Col className="col-8">
                {uploading === true ? (
                  <CustomSpinner progress={cCprogress} />
                ) : (
                  ''
                )}

                {camLoader ? (
                  <div className="camLoader">
                    <Spin size="large" />
                    {/* <CustomSpinner /> */}
                  </div>
                ) : (
                  <div className="companyCam-single-project-details">
                    <h2>{companyCamHeading}</h2>
                    <div
                      className="form-check"
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingRight: '25px',
                      }}
                    >
                      <h2>
                        <Checkbox
                          onChange={handleCheckAllChange}
                          checked={
                            selectedImages.length === companyCamProImg.length
                          }
                        >
                          Select all
                        </Checkbox>
                      </h2>
                    </div>
                    <p>
                      <span>{CompanycamAddressStreet}</span>
                      {CompanycamAddressStreet && ', '}
                      <span>{CompanycamAddressCity}</span>
                      {CompanycamAddressCity && ', '}
                      <span>{CompanycamAddressState}</span>
                      {CompanycamAddressState && ', '}
                      <span>{CompanycamAddressPostal}</span>
                    </p>
                  </div>
                )}
                <Row className="my-2 footerRow" gutter={6}>
                  {companyCamProImg.length > 0 ? (
                    <ul className="Companyproject_images-outer">
                      {Object.keys(companyCamProImg).map((keyName, index) => (
                        <>
                          <li className="Companyproject_images-box">
                            <input
                              type="checkbox"
                              className="checkboxstyle"
                              id={'checkbox' + index}
                              key={companyCamProImg[keyName]?.id}
                              value={companyCamProImg[keyName]?.uris[3]?.uri}
                              onChange={hadleChangeCompanycam}
                              checked={selectedImages?.includes(
                                companyCamProImg[keyName]?.uris[3]?.uri
                              )}
                            />
                            <label htmlFor={'checkbox' + index}>
                              <div className="logoDiv companyCam">
                                <Image
                                  src={companyCamProImg[keyName]?.uris[3]?.uri}
                                  className="img-fluid"
                                  alt="logos"
                                  preview={false}
                                />
                              </div>
                            </label>
                          </li>
                        </>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <SmileOutlined style={{ fontSize: 20 }} />
                      <p>Please select a project from sidebar!</p>
                    </div>
                  )}
                </Row>
              </Col>
            </Row>
          </div>

          <div className="modelButtons">
            <Button className="btnPrimary" onClick={handleCancelModal}>
              Cancel
            </Button>
            <Button
              className="btnPrimary"
              htmlType="submit"
              disabled={uploading}
            >
              Upload and Exit
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ADD TOKEN MODEL  */}
      <Modal
        // title={"CompanyCam's Projects" }
        centered
        open={isModalToken}
        className="inspectionCategoryModal industryModal customIndustryModal companyCamModel"
        width={1000}
        footer={null}
        onOk={handleOkToken}
        onCancel={handleCancelToken}
        closable={true}
      >
        <Row>
          <Col xs={24} md={24}>
            <div className="modal-header">
              <div className="modal-header-title">
                <Title level={4}>Add CompanyCam Token</Title>
              </div>
              <div className="modal-header-logo">
                <Image
                  src="https://d33v4339jhl8k0.cloudfront.net/docs/assets/5e94d6a504286364bc984894/images/630d36a64cde766bbe140183/Full-Color-Over-White.png"
                  className="img-fluid"
                  alt="logos"
                  preview={false}
                />
              </div>
            </div>
          </Col>
        </Row>
        <Form
          name="companyCamforms"
          layout="vertical"
          autoComplete="off"
          form={form2}
          onFinish={setCompanycamToken}
        >
          <div className="companyToken-inst">
            <Row className="my-2 footerRow" gutter={6}>
              <Col xs={24} md={24}>
                <h2 className="companyToken-title">
                  Please input your Companycam token
                </h2>
                <div className="companyToken-form">
                  <Row gutter={16} className="companyToken-row">
                    <Col xs={24} md={18} xl={18}>
                      <Item
                        name="companycam_token"
                        placeholder="Please input your Companycam token"
                        rules={[
                          {
                            required: true,
                            message: 'Please input your Companycam token',
                          },
                          {
                            min: 40,
                            message: 'Please enter a valid token',
                          },
                          {
                            max: 49,
                            message: 'Please enter a valid token',
                          },
                        ]}
                      >
                        <Input
                          placeholder="Please input your Companycam token"
                          className="formControl"
                          defaultValue={companyCamToken}
                        />
                      </Item>
                      {companyCamToken && companyCam.length === 0 ? (
                        <Result
                          status="error"
                          title="Your Token might be expired or invalid."
                        />
                      ) : (
                        ''
                      )}
                    </Col>
                    <Col xs={24} md={6} xl={6}>
                      <Form.Item>
                        <Button
                          className="btnPrimary"
                          type="primary"
                          htmlType="submit"
                        >
                          Submit
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>
        </Form>
        <div className="helpModel-heading">
          <h3>How to create company cam access token?</h3>
          <Button className="btnPrimary" onClick={showHelpModal}>
            Help
          </Button>
        </div>
      </Modal>

      {/* HELP TOKEN MODEL  */}
      <Modal
        // title={"CompanyCam's Projects" }
        centered
        open={isModalHelp}
        className="inspectionCategoryModal industryModal customIndustryModal companyCamModel HelpTokenModel"
        width={1000}
        footer={null}
        onOk={handleOkHelp}
        onCancel={handleCancelHelp}
        closable={false}
      >
        <Row>
          <Col xs={24} md={24}>
            <div className="modal-header">
              <div className="modal-header-title"></div>
              <div className="modal-header-logo">
                <Image
                  src="https://d33v4339jhl8k0.cloudfront.net/docs/assets/5e94d6a504286364bc984894/images/630d36a64cde766bbe140183/Full-Color-Over-White.png"
                  className="img-fluid"
                  alt="logos"
                  preview={false}
                />
              </div>
            </div>
          </Col>
        </Row>

        <div>
          <Row className="my-2 footerRow" gutter={6}>
            <Col xs={24} md={24}>
              <section className="timeline customAntImageWrapper">
                <h1>How To Generate CompanyCam Access Token?</h1>
                <Image
                  src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/05/19041149/ezgif-1-346a85312d.gif"
                  className="img-fluid"
                  alt="images"
                  preview={false}
                />
              </section>
            </Col>
          </Row>
        </div>
        <div className="modelButtons">
          <Button
            className="btnPrimary"
            htmlType="submit"
            onClick={handleButtonClick}
          >
            Generate Token
          </Button>
          <Button
            className="btnPrimary"
            htmlType="submit"
            onClick={handleCancelHelp}
          >
            Ok
          </Button>
        </div>
      </Modal>
      {/* FdCam Model */}
      <Modal
        centered
        open={isFDCamModalOpen}
        className="inspectionCategoryModal industryModal customIndustryModal companyCamModel"
        width={1000}
        footer={null}
        onOk={handleFDCamOk}
        onCancel={handleCancelFDCamModal}
        closable={false}
      >
        <Row>
          <Col xs={24} md={24}>
            <div className="modal-header">
              <div className="modal-header-title">
                <Title level={4}>FDCam's Projects</Title>
              </div>
              <div className="modal-header-logo model-logo-center">
                <Image
                  src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2021/08/21062258/no-image-165fc0a7fd134865fc0a7fd1f97.jpg"
                  className="img-fluid fd-cam-model-logo"
                  alt="logos"
                  preview={false}
                />
              </div>
            </div>
          </Col>
        </Row>
        <Form name="fdCamForm" layout="vertical" autoComplete="off" form={fdForm} onFinish={fdCamModalType === 'photos' ? saveFDCamMedia : saveFDCamVideos}>
          <div>
          
            <Row className="my-2 footerRow fd-cam-project-model" gutter={6}>
              <Col className="col-4 position-relative"
                ref={sidebarRef}
                onScroll={handleScroll}
                style={{
                  maxHeight: '100vh',
                  overflowY: 'auto',
                  paddingRight: '10px'
                }}
              >
                <div className='fd-cam-search'>
                <Search placeholder="Find a project..." allowClear onSearch={handleSearch} />
                 </div>
                 
                
                {projects.length > 0 ? (
                  <>
                
                  {projects.map((projectitem) => (
                    <div className="project-box-key" onClick={() => handleSelectFdCamSelect(projectitem)} key={projectitem.id}>
                      <div className={projectitem?.id === fdCamSelectedProject?.id ? "active_fd_cam_project fd-camproject-box" : "fd-camproject-box"}>
                        <div className="row">
                          <div className="col-md-5 col-sm-12 fd-camproject-box-image">
                          
                            <div className="project-card-image">
                             
                              {projectitem?.inspection_images?.length > 0 ? (
                                <img className="pro-folder-img" alt="folder-image" src={projectitem?.inspection_images[0]?.image_url} />
                              ) : (
                                <img className="pro-folder-img-icon" alt="folder-image" src={FolderIMage} />
                              )}
                            </div>
                          </div>
                          <div className="col-md-7 col-sm-12 fd-camproject-box-content">
                            <h4 className="card-meta-title">{projectitem?.name}</h4>
                            <p className="project-location">
                              <b>Address</b> :- {projectitem?.address?.trim() ? projectitem.address : projectitem?.street}, {projectitem?.city}, {projectitem?.state}, {projectitem?.postal_code}
                            </p>
                            <div className="project-inner-item">
                              {fdCamModalType === 'photos' ? (
                                <p className="pro-item-count"><b>Photos</b> :- {projectitem?.inspection_images?.length || 0}</p>
                              ) : (
                                <p className="pro-item-count"><b>Videos</b> :- {projectitem?.inspection_videos?.length || 0}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                   {loading ? (<div className='fd-cam-project-loading-section text-center'><span className='text-center'>Please wait, the project is loading...</span><br/><Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} /></div>) : (<></>)}
                  </>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <SmileOutlined style={{ fontSize: 20 }} />
                    <p>No projects available!</p>
                  </div>
                )}
              </Col>
              <Col className="col-8 px-3 padding-col-right-0">
                <div className="fdCam-single-project-images">
                  {fdCamLoader ? (
                    <Skeleton active paragraph={{ rows: 4 }} />
                  ) : fdCamSelectedProject ? (
                    <>
                      <p className="fDCam-single-project-detail-title">{fdCamSelectedProject?.name}</p>
                      <p className="fDCam-single-project-detail-address">
                        {fdCamSelectedProject?.address?.trim() ? fdCamSelectedProject.address : fdCamSelectedProject?.street}, {fdCamSelectedProject?.city}, {fdCamSelectedProject?.state}, {fdCamSelectedProject?.postal_code}
                      </p>


                      {fdCamModalType === 'photos' ? (
                        <>
                          <div className="text-end my-2">
                            <Checkbox checked={selectImagesFDCam} onChange={(e) => handleFdCamCheck(e, fdCamSelectedProject)}><span className="select-all-text">Select All</span></Checkbox>
                          </div>
                          <Row>
                            {fdCamSelectedProject?.inspection_images?.map((project) => (
                              <Col className="px-2 mobile-full-width" xl={6} md={8} sm={12} key={project?.id}>
                                <div className="fd-cam-single-checkbox-image">
                                  <Checkbox
                                    checked={fDCamImages.some(image => image.id === project?.id)}
                                    onChange={() => handleImageCheck(project?.id)}
                                  >
                                    <Image
                                      preview={false} src={project?.image_url}
                                      placeholder={
                                        <Image
                                          preview={false}
                                          src={project?.thumb_url}
                                        />
                                      }
                                    />
                                  </Checkbox>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </>
                      ) : (
                        <>
                          <div className="text-end my-2">
                            <Checkbox checked={selectVideosFDCam} onChange={(e) => handleVideoSelectAll(e, fdCamSelectedProject)}><span className="select-all-text">Select All</span></Checkbox>
                          </div>
                          <Row>
                              {fdCamSelectedProject?.inspection_videos?.length > 0 ? (
                                fdCamSelectedProject?.inspection_videos?.map((video) => (
                                <Col className="px-2 mobile-full-width" xl={6} md={8} sm={12} key={video.id}>
                                  <div className="fd-cam-single-checkbox-image video-modal-item">
                                    <Checkbox
                                      className="fd-cam-modal-checkbox"
                                      checked={fdCamSelectedVideos.some(v => v.id === video.id)}
                                      onChange={() => handleVideoCheck(video.id)}
                                    />
                                    <div className="video-item-preview-modal-v2">
                                      <video
                                        src={`${video.source_url}#t=0.001`}
                                        poster={video.thumb_url || video.thumbnail || video.video_thumbnail || video.thumbnail_url || video.thumb}
                                        preload="metadata"
                                        muted
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000', pointerEvents: 'none' }}
                                      />
                                      <div className="video-play-overlay-modal-v2">
                                        <PlayCircleOutlined />
                                      </div>
                                    </div>
                                  </div>
                                </Col>
                              ))
                              ) : (
                                <Col span={24} className="text-center my-4">
                                  <div className="no-video-alert p-4 border bg-light rounded">
                                      <VideoCameraOutlined style={{ fontSize: '32px', color: '#8c8c8c', marginBottom: '10px' }} />
                                      <p className="m-0 text-muted">No videos found for this project.</p>
                                  </div>
                                </Col>
                            )}
                          </Row>
                        </>
                      )}
                    </>
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <SmileOutlined style={{ fontSize: 20 }} />
                      <p>Please select a project from the sidebar!</p>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
          <div className="modelButtons">
            <Button className="btnPrimary" onClick={handleCancelFDCamModal}>Cancel</Button>
            <Tooltip 
              placement="top"
              title={(fdCamModalType === 'photos' ? fDCamImages.length === 0 : fdCamSelectedVideos.length === 0) ? `Please select at least one ${fdCamModalType === 'photos' ? 'photo' : 'video'} to continue` : null}
            >
              <span className="fd-cam-upload-button-wrapper">
                <Button 
                  className="btnPrimary" 
                  htmlType="submit" 
                  disabled={uploading || (fdCamModalType === 'photos' ? fDCamImages.length === 0 : fdCamSelectedVideos.length === 0)}
                >
                  Upload and Exit
                </Button>
              </span>
            </Tooltip>
          </div>
        </Form>
      </Modal>

    </>
  );
};

export default App;
